/**
 * Dialling countries for the login phone selector (screen 1h).
 *
 * The default is Somalia (+252) — the pilot's market — but a customer elsewhere
 * (diaspora, regional fleets) can pick their own. The field emits
 * `+<dial><national>` and raad-api's `normalisePhone` (libphonenumber-js, SO
 * default region) collapses it to canonical E.164. Verified against the real
 * dependency: `+252` + `612345678` and `+252` + `0612345678` BOTH normalise to
 * `+252612345678` — byte-identical to the pre-selector national-only submission,
 * so adding the selector does not change the wire identity for a Somali number.
 *
 * Names resolve through i18n (`login.country.<iso>`) — both EN and SO, like every
 * other user-facing string. `dial` is the E.164 country calling code (no `+`).
 */
export interface Country {
  /** ISO 3166-1 alpha-2, also the i18n key suffix. */
  readonly iso: string;
  /** E.164 calling code without the leading '+'. */
  readonly dial: string;
  /** Flag emoji (regional-indicator pair). */
  readonly flag: string;
}

/**
 * East Africa first (the real fleets), then the common Somali-diaspora
 * countries. Ordered for a Mogadishu fleet manager: their country is the
 * default and sits at the top.
 */
export const COUNTRIES: readonly Country[] = [
  { iso: 'SO', dial: '252', flag: '🇸🇴' },
  { iso: 'KE', dial: '254', flag: '🇰🇪' },
  { iso: 'ET', dial: '251', flag: '🇪🇹' },
  { iso: 'DJ', dial: '253', flag: '🇩🇯' },
  { iso: 'UG', dial: '256', flag: '🇺🇬' },
  { iso: 'TZ', dial: '255', flag: '🇹🇿' },
  { iso: 'SS', dial: '211', flag: '🇸🇸' },
  { iso: 'AE', dial: '971', flag: '🇦🇪' },
  { iso: 'TR', dial: '90', flag: '🇹🇷' },
  { iso: 'GB', dial: '44', flag: '🇬🇧' },
  { iso: 'SE', dial: '46', flag: '🇸🇪' },
  { iso: 'US', dial: '1', flag: '🇺🇸' },
  { iso: 'CA', dial: '1', flag: '🇨🇦' },
] as const;

export const DEFAULT_COUNTRY_ISO = 'SO';

export function countryByIso(iso: string): Country {
  return COUNTRIES.find((c) => c.iso === iso) ?? COUNTRIES[0]!;
}
